import { FunctionComponent, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import Button from '../button';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import { CatalogOption, CatalogOptionType } from '@src/types/catalogOption';

import './index.scss';

const MAX_DETAIL_LENGTH = 150;

interface Props {
  claimUId: string;
  countryCode: string;
  insurerId: number;
  /** Se invoca tras guardar una nota con éxito (para refrescar el historial). */
  onNoteAdded?: () => void;
}

/**
 * HU-04 (CLAIM-3707): formulario para agregar una nota de Análisis (Purdy CR).
 * Reutiliza la estructura visual del seguimiento de Subrogación (dropdown +
 * detalle + Agregar) pero guarda en el historial de Análisis con el tag
 * [ANÁLISIS]. No modifica el componente de Subrogación PR.
 */
const AnalysisNote: FunctionComponent<Props> = ({
  claimUId,
  countryCode,
  insurerId,
  onNoteAdded,
}): JSX.Element => {
  const [noteTypes, setNoteTypes] = useState<CatalogOption[]>([]);
  const [noteTypeCode, setNoteTypeCode] = useState<string>('');
  const [detail, setDetail] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [catalogError, setCatalogError] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const loadNoteTypes = async () => {
      try {
        setCatalogError(false);
        const options = await new ClaimsTrackerClient().getCatalogOptions(
          CatalogOptionType.NOTE_TYPE,
          countryCode,
          insurerId,
        );

        if (!options || options.length === 0) {
          setCatalogError(true);
          setNoteTypes([]);

          return;
        }

        setNoteTypes(options);
      } catch (error) {
        console.error('Error loading note types catalog:', error);
        setCatalogError(true);
        setNoteTypes([]);
      }
    };

    loadNoteTypes();
  }, [countryCode, insurerId]);

  const handleDetailChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Recorta a 150 aunque el usuario pegue un texto más largo.
    setDetail(event.target.value.slice(0, MAX_DETAIL_LENGTH));
  };

  const isAddDisabled =
    catalogError || saving || !noteTypeCode || detail.trim().length === 0;

  const handleAdd = async () => {
    if (isAddDisabled) {
      return;
    }

    setSaving(true);
    try {
      await new ClaimsTrackerClient().registerAnalysisNote(
        claimUId,
        noteTypeCode,
        detail.trim(),
        Number(localStorage.getItem('userId')),
      );

      // Limpiar campos tras guardar con éxito.
      setNoteTypeCode('');
      setDetail('');
      setFeedback({
        open: true,
        message: 'Nota agregada correctamente.',
        severity: 'success',
      });

      onNoteAdded?.();
    } catch (error) {
      console.error('Error saving analysis note:', error);
      // Conservar tipo y detalle para reintentar.
      setFeedback({
        open: true,
        message: 'No se pudo guardar la nota. Intente nuevamente.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="analysis-note">
      <label className="analysis-note__title">SEGUIMIENTO DE ANÁLISIS</label>
      {catalogError && (
        <Alert severity="error" className="analysis-note__alert">
          No se pudieron cargar los tipos de nota.
        </Alert>
      )}
      <Grid className="analysis-note__row">
        <FormControl
          sx={{ minWidth: 235, backgroundColor: '#ffffff' }}
          disabled={catalogError}
        >
          <InputLabel id="analysis-note-type">Tipo de nota</InputLabel>
          <Select
            labelId="analysis-note-type"
            value={noteTypeCode}
            onChange={(event) => setNoteTypeCode(event.target.value)}
            label="Tipo de nota"
          >
            {noteTypes.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <br />
      <Grid className="analysis-note__row analysis-note__row--detail">
        <TextField
          className="analysis-note__input"
          label="Detalle"
          value={detail}
          onChange={handleDetailChange}
          disabled={catalogError}
          multiline
          inputProps={{ maxLength: MAX_DETAIL_LENGTH }}
          helperText={`${detail.length}/${MAX_DETAIL_LENGTH}`}
        />
        <Button
          className="analysis-note__add"
          disabled={isAddDisabled}
          text={saving ? 'Agregando...' : 'AGREGAR'}
          type="button"
          color="secondary"
          onClick={handleAdd}
        />
      </Grid>
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          severity={feedback.severity}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnalysisNote;
